import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { 
  trackButtonClick, 
  trackSocialShare, 
  trackAuthAction, 
  trackPropertyInteraction, 
  trackNavigationEnhanced, 
  trackFormInteraction, 
  trackModalInteraction, 
  trackSearchEnhanced, 
  trackContactAction, 
  trackFileInteraction, 
  trackWishlistAction, 
  trackPaginationAction 
} from '../lib/analytics';

/**
 * Custom hook for enhanced Google Analytics tracking
 * Provides convenient methods for tracking user interactions with automatic context
 */
export const useAnalytics = () => {
  const { user } = useAuth();
  
  const getUserType = useCallback(() => {
    if (!user) return 'guest';
    return user.user_metadata?.user_type || user.user_metadata?.role || 'user';
  }, [user]);

  const getUserId = useCallback(() => {
    return user?.id || 'anonymous';
  }, [user]);

  // Enhanced button click tracking with automatic user context
  const trackClick = useCallback((
    buttonName: string, 
    location?: string, 
    additionalData?: Record<string, any>
  ) => {
    const currentLocation = location || window.location.pathname;
    trackButtonClick(buttonName, currentLocation, getUserType(), {
      user_id: getUserId(),
      ...additionalData,
    });
  }, [getUserType, getUserId]);

  // Social sharing tracking
  const trackShare = useCallback((
    platform: string, 
    contentType: string, 
    contentId?: string, 
    propertyId?: string
  ) => {
    trackSocialShare(platform, contentType, contentId, propertyId);
  }, []);

  // Authentication tracking
  const trackAuth = useCallback((
    action: 'login' | 'logout' | 'register' | 'password_reset' | 'social_login', 
    provider?: string
  ) => {
    trackAuthAction(action, provider, getUserType());
  }, [getUserType]);

  // Property interaction tracking
  const trackProperty = useCallback((
    action: string, 
    propertyId: string, 
    propertyType?: string
  ) => {
    trackPropertyInteraction(action, propertyId, propertyType, getUserType());
  }, [getUserType]);

  // Navigation tracking
  const trackNavigation = useCallback((
    destination: string, 
    source?: string, 
    method?: string
  ) => {
    const currentSource = source || window.location.pathname;
    trackNavigationEnhanced(destination, currentSource, getUserType(), method);
  }, [getUserType]);

  // Form tracking
  const trackForm = useCallback((
    formName: string, 
    action: 'start' | 'submit' | 'error' | 'abandon', 
    fieldName?: string
  ) => {
    trackFormInteraction(formName, action, fieldName, getUserType());
  }, [getUserType]);

  // Modal tracking
  const trackModal = useCallback((
    modalName: string, 
    action: 'open' | 'close' | 'submit'
  ) => {
    trackModalInteraction(modalName, action, getUserType());
  }, [getUserType]);

  // Search tracking
  const trackSearch = useCallback((
    searchTerm: string, 
    filterData?: Record<string, any>, 
    resultsCount?: number, 
    location?: string
  ) => {
    trackSearchEnhanced(searchTerm, filterData, resultsCount, getUserType(), location);
  }, [getUserType]);

  // Contact tracking
  const trackContact = useCallback((
    action: string, 
    contactType: string, 
    propertyId?: string, 
    recipientType?: string
  ) => {
    trackContactAction(action, contactType, propertyId, recipientType, getUserType());
  }, [getUserType]);

  // File interaction tracking
  const trackFile = useCallback((
    action: 'download' | 'view' | 'upload', 
    fileName: string, 
    fileType: string
  ) => {
    trackFileInteraction(action, fileName, fileType, getUserType());
  }, [getUserType]);

  // Wishlist tracking
  const trackWishlist = useCallback((
    action: 'add' | 'remove' | 'view', 
    propertyId: string
  ) => {
    trackWishlistAction(action, propertyId, getUserType());
  }, [getUserType]);

  // Pagination tracking
  const trackPagination = useCallback((
    action: 'next' | 'previous' | 'page_jump', 
    currentPage: number, 
    totalPages: number, 
    listingType?: string
  ) => {
    trackPaginationAction(action, currentPage, totalPages, listingType);
  }, []);

  // Quick tracking methods for common actions
  const quickTrack = {
    // Navigation buttons
    home: () => trackClick('home_button', 'navigation'),
    back: () => trackClick('back_button', 'navigation'),
    signIn: () => trackClick('sign_in_button', 'auth'),
    signUp: () => trackClick('sign_up_button', 'auth'),
    signOut: () => trackClick('sign_out_button', 'auth'),
    
    // Property actions
    viewProperty: (propertyId: string) => {
      trackClick('view_property_button', 'property_listing');
      trackProperty('view_details', propertyId);
    },
    contactAgent: (propertyId: string) => {
      trackClick('contact_agent_button', 'property_detail');
      trackContact('contact_agent', 'property_inquiry', propertyId, 'agent');
    },
    sendMessage: (propertyId: string) => {
      trackClick('send_message_button', 'property_detail');
      trackContact('send_message', 'direct_message', propertyId);
    },
    addToWishlist: (propertyId: string) => {
      trackClick('add_wishlist_button', 'property_detail');
      trackWishlist('add', propertyId);
    },
    removeFromWishlist: (propertyId: string) => {
      trackClick('remove_wishlist_button', 'property_detail');
      trackWishlist('remove', propertyId);
    },
    
    // Search and filter actions
    searchSubmit: (searchTerm: string) => {
      trackClick('search_submit_button', 'search');
      trackSearch(searchTerm);
    },
    filterApply: (filterData: Record<string, any>) => {
      trackClick('filter_apply_button', 'search');
      trackSearch('', filterData);
    },
    filterReset: () => trackClick('filter_reset_button', 'search'),
    
    // Social sharing
    shareWhatsApp: (propertyId?: string) => {
      trackClick('share_whatsapp_button', 'social_share');
      trackShare('whatsapp', 'property', propertyId, propertyId);
    },
    shareFacebook: (propertyId?: string) => {
      trackClick('share_facebook_button', 'social_share');
      trackShare('facebook', 'property', propertyId, propertyId);
    },
    shareInstagram: (propertyId?: string) => {
      trackClick('share_instagram_button', 'social_share');
      trackShare('instagram', 'property', propertyId, propertyId);
    },
    shareTikTok: (propertyId?: string) => {
      trackClick('share_tiktok_button', 'social_share');
      trackShare('tiktok', 'property', propertyId, propertyId);
    },
    shareViber: (propertyId?: string) => {
      trackClick('share_viber_button', 'social_share');
      trackShare('viber', 'property', propertyId, propertyId);
    },
    
    // Pagination
    nextPage: (currentPage: number, totalPages: number) => {
      trackClick('pagination_next_button', 'pagination');
      trackPagination('next', currentPage, totalPages);
    },
    prevPage: (currentPage: number, totalPages: number) => {
      trackClick('pagination_prev_button', 'pagination');
      trackPagination('previous', currentPage, totalPages);
    },
    goToPage: (page: number, totalPages: number) => {
      trackClick(`pagination_page_${page}_button`, 'pagination');
      trackPagination('page_jump', page, totalPages);
    },
    
    // Modal actions
    openModal: (modalName: string) => {
      trackClick(`open_${modalName}_modal_button`, 'modal');
      trackModal(modalName, 'open');
    },
    closeModal: (modalName: string) => {
      trackClick(`close_${modalName}_modal_button`, 'modal');
      trackModal(modalName, 'close');
    },
    submitModal: (modalName: string) => {
      trackClick(`submit_${modalName}_modal_button`, 'modal');
      trackModal(modalName, 'submit');
    },
    
    // Form actions
    startForm: (formName: string) => trackForm(formName, 'start'),
    submitForm: (formName: string) => {
      trackClick(`submit_${formName}_form_button`, 'form');
      trackForm(formName, 'submit');
    },
    
    // File actions
    downloadFile: (fileName: string, fileType: string) => {
      trackClick('download_file_button', 'file_action');
      trackFile('download', fileName, fileType);
    },
    uploadFile: (fileName: string, fileType: string) => {
      trackClick('upload_file_button', 'file_action');
      trackFile('upload', fileName, fileType);
    },
  };

  return {
    trackClick,
    trackShare,
    trackAuth,
    trackProperty,
    trackNavigation,
    trackForm,
    trackModal,
    trackSearch,
    trackContact,
    trackFile,
    trackWishlist,
    trackPagination,
    quickTrack,
    getUserType,
    getUserId,
  };
};

export default useAnalytics;